import { component$ } from "@builder.io/qwik";
import { Card, CardHeader, CardContent, CardFooter } from "./ui/card";
import { cn } from "~/lib/cn";

export interface CoachCardSkeletonProps {
  seed?: number;
  class?: string;
}

export const CoachCardSkeleton = component$<CoachCardSkeletonProps>(
  ({ seed = 0, class: className }) => {
    // Create deterministic variations based on seed
    const getVariation = (base: number, variance: number, seedOffset = 0) => {
      // Simple pseudo-random function based on seed
      const pseudoRandom = Math.sin(seed + seedOffset) * 10000;
      const normalizedRandom =
        (pseudoRandom - Math.floor(pseudoRandom)) * 2 - 1; // Range -1 to 1
      return base + normalizedRandom * variance;
    };

    // Generate variations for different elements
    const nameWidth = `${Math.floor(getVariation(75, 15, 1))}%`;
    const titleWidth = `${Math.floor(getVariation(85, 10, 2))}%`;
    const brandNameWidth = `${Math.floor(getVariation(100, 30, 3))}px`;
    const specialtyWidth = `${Math.floor(getVariation(90, 10, 4))}%`;
    const desc1Width = `${Math.floor(getVariation(95, 10, 5))}%`;
    const desc2Width = `${Math.floor(getVariation(85, 15, 6))}%`;
    const desc3Width = `${Math.floor(getVariation(75, 15, 7))}%`;

    // Determine if brand section should be shown
    const showBrand = getVariation(0.5, 0.5, 8) > 0;

    return (
      <Card
        class={cn("relative flex h-96 flex-col justify-between", className)}
      >
        <div class="flex h-full animate-pulse flex-col [animation-duration:2s]">
          <CardHeader class="p-4">
            <div class="flex gap-4">
              {/* Profile Image */}
              <div class="h-20 w-20 shrink-0 grow-0">
                <div class="h-full w-full rounded-full bg-gray-400/20" />
              </div>
              <div class="flex grow flex-col justify-center">
                {/* Name */}
                <div class="h-6" style={{ width: nameWidth }}>
                  <div class="h-full w-full rounded bg-gray-400/20" />
                </div>
                {/* Title */}
                <div class="mt-1 h-4" style={{ width: titleWidth }}>
                  <div class="h-full w-full rounded bg-gray-400/20" />
                </div>
              </div>
            </div>
            {/* Brand logo and name */}
            {showBrand && (
              <div class="flex">
                <div class="mt-2 flex w-full items-center justify-center gap-x-2 pl-4">
                  <div class="h-8 w-8 rounded-full border bg-gray-400/20" />
                  <div
                    class="h-5 rounded bg-gray-400/20"
                    style={{ width: brandNameWidth }}
                  />
                </div>
              </div>
            )}
          </CardHeader>

          <CardContent class="grow p-4 pt-0">
            <div class="flex flex-col gap-1.5">
              {/* Specialties */}
              <div class="flex gap-1">
                <div class="h-4 w-4 rounded bg-gray-400/20" />
                <div
                  class="h-4 flex-1 rounded bg-gray-400/20"
                  style={{ width: specialtyWidth }}
                />
              </div>
              {/* Title/Description */}
              <div class="mt-1 flex gap-1">
                <div class="h-4 w-4 rounded bg-gray-400/20" />
                <div class="flex-1">
                  <div
                    class="h-4 w-full rounded bg-gray-400/20"
                    style={{ width: desc1Width }}
                  />
                  <div
                    class="mt-1 h-4 rounded bg-gray-400/20"
                    style={{ width: desc2Width }}
                  />
                  <div
                    class="mt-1 h-4 rounded bg-gray-400/20"
                    style={{ width: desc3Width }}
                  />
                </div>
              </div>
            </div>
          </CardContent>

          <CardFooter class="p-4 pt-0">
            <div class="border-secondary h-10 w-full rounded-md bg-gray-400/20" />
          </CardFooter>
        </div>
      </Card>
    );
  },
);
